import { useState } from 'react'

import { Box, List, Section, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import EmployeeLabelSupervisor from 'modules/reviews/components/EmployeeLabel/EmployeeLabelSupervisor'
import AddEditorModal from 'modules/reviews/components/ReviewSettings/Editors/AddEditorModal'
import type Review from 'modules/reviews/store/models/v2/Review'

import EditorRightActions from './EditorRightActions'

interface ItemProps {
  supervisorId: number
  review: Review
  authorId: number
}

function Item({ supervisorId, review, authorId }: ItemProps) {
  const user = users.find((user) => user.id === supervisorId)
  if (!user) return null

  const userIsAuthor = user.id === authorId
  const {
    access: { id: userId },
  } = useCurrentSession()
  const currentUserIsAuthor = userIsAuthor && userId === authorId

  return (
    <List.Item>
      <Box key={user.attributes.id} paddingY="s16">
        <EmployeeLabelSupervisor user={user} />
      </Box>
      {!currentUserIsAuthor && (
        <EditorRightActions
          userIsAuthor={userIsAuthor}
          supervisorId={supervisorId}
          review={review}
        />
      )}
    </List.Item>
  )
}

interface ItemsProps {
  supervisorsIds: number[]
  review: Review
  authorId: number
}

const Items = observer(({ supervisorsIds, review, authorId }: ItemsProps) => {
  if (!supervisorsIds.length) {
    return (
      <Box aria-label="List of editors">
        <Text.Normal>{i18n.t('surveys.form.managers.blank_slate')}</Text.Normal>
      </Box>
    )
  }

  return (
    <Box aria-label="List of editors">
      <List>
        {supervisorsIds.map((supervisorId) => (
          <Item
            key={supervisorId}
            supervisorId={supervisorId}
            review={review}
            authorId={authorId}
          />
        ))}
      </List>
    </Box>
  )
})

interface Props {
  review: Review
  contractVersions: ContractVersions
}

function EditorsSection({ review, contractVersions }: Props) {
  const [showNewEditorModal, setShowNewEditorModal] = useState(false)

  const authorId = users.find({
    id: review.attributes.author_id,
  })?.id
  const addEditor = {
    onClick: () => setShowNewEditorModal(true),
    label: i18n.t('surveys.form.managers.add_manager'),
  }

  return (
    <>
      <Section
        illustration="people-add"
        title={i18n.t('surveys.form.managers.title')}
        description={i18n.t('surveys.form.managers.description')}
        action={addEditor}
      >
        <Items
          supervisorsIds={review.attributes.supervisor_ids}
          review={review}
          authorId={authorId}
        />
      </Section>
      {showNewEditorModal && (
        <AddEditorModal
          review={review}
          onClose={() => setShowNewEditorModal(false)}
          contractVersions={contractVersions}
        />
      )}
    </>
  )
}

export default observer(EditorsSection)
